/**
 * Authentication Context with Supabase
 * Manages user authentication state across the application
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, authService } from '../services/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load session on mount and listen for auth changes
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // If we have a session, fetch the profile
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users_pkks')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      const result = await authService.login(email, password);
      
      if (result.error) {
        return { 
          success: false, 
          error: result.error.message || 'Login failed' 
        };
      }
      
      // State will be updated by onAuthStateChange listener
      return { success: true, user: result.user, profile: result.profile };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Login failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      setLoading(true);
      const result = await authService.signup(userData);
      
      if (result.error) {
        return { 
          success: false, 
          error: result.error.message || 'Signup failed' 
        };
      }
      
      return { success: true, user: result.user, profile: result.profile };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Signup failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      const { error } = await authService.logout();
      
      if (error) {
        console.error('Logout error:', error);
      }
      
      // State will be cleared by onAuthStateChange listener
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      const result = await authService.forgotPassword(email);
      
      if (result.error) {
        return { 
          success: false, 
          error: result.error.message || 'Failed to send reset email' 
        };
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Failed to send reset email' 
      };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const result = await authService.updatePassword(newPassword);
      
      if (result.error) {
        return { 
          success: false, 
          error: result.error.message || 'Failed to update password' 
        };
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Failed to update password' 
      };
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    isAuthenticated: !!user,
    role: profile?.role,
    login,
    signup,
    logout,
    forgotPassword,
    updatePassword,
    isPrincipal: profile?.role === 'principal',
    isTeacher: profile?.role === 'teacher',
    isStudent: profile?.role === 'student',
    isIndustryPartner: profile?.role === 'industry_partner',
    isParent: profile?.role === 'parent',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
